import React from 'react';
import {
  IndianRupee, TrendingUp, Calendar, ArrowUpRight, Clock, CheckCircle2, 
  Coffee, Truck, Ban, XCircle, Package, ShoppingCart
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { cn } from 'lib/utils';

const StatCard = ({ title, value, icon: Icon, color, highlight = false }) => {
  const colors = {
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-900/30",
    emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30",
    red: "bg-red-100 text-red-700 dark:bg-red-900/30",
    green: "bg-green-100 text-green-700 dark:bg-green-900/30",
    purple: "bg-purple-100 text-purple-700 dark:bg-purple-900/30",
    orange: "bg-orange-100 text-orange-700 dark:bg-orange-900/30",
    indigo: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30",
  };

  return (
    <div className={cn(
      "rounded-2xl p-5 border shadow-sm transition-all hover:shadow-md",
      highlight && "ring-2 ring-primary/20 bg-gradient-to-br from-primary/5"
    )}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground font-medium">{title}</p>
          <p className={cn("text-2xl font-bold mt-2", highlight && "text-primary")}>
            {value}
          </p>
        </div>
        <div className={cn("p-3 rounded-full", colors[color] || colors.blue)}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};

const StatusProgress = ({ stats, total }) => (
  <div className="space-y-3 bg-muted/30 rounded-xl p-5">
    <p className="text-sm font-medium text-muted-foreground">Order Status Distribution</p>
    <div className="space-y-2">
      {[
        { label: "Pending", value: stats.pending || 0, color: "bg-amber-500" },
        { label: "Preparing", value: stats.preparing || 0, color: "bg-blue-500" },
        { label: "Ready", value: stats.ready || 0, color: "bg-orange-500" },
        { label: "Delivered", value: stats.delivered || 0, color: "bg-emerald-500" },
        { label: "Cancelled", value: stats.cancelled || 0, color: "bg-red-500" },
      ].map((item) => (
        <div key={item.label} className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground w-20">{item.label}</span>
          <div className="flex-1 bg-muted rounded-full h-6 overflow-hidden">
            <div
              className={cn("h-full transition-all duration-700", item.color)}
              style={{ width: `${total > 0 ? (item.value / total) * 100 : 0}%` }}
            />
          </div>
          <span className="text-sm font-medium w-12 text-right">{item.value}</span>
        </div>
      ))}
    </div>
  </div>
);

const TodayStatusBars = ({ stats }) => (
  <div className="bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-950/20 dark:to-blue-950/20 rounded-xl p-5">
    <p className="text-sm font-semibold mb-4">Today's Flow</p>
    <div className="grid grid-cols-5 gap-2 text-center">
      {[
        { label: "Pending", value: stats.today_total_pending || 0, color: "bg-amber-400" },
        { label: "Prep", value: stats.today_total_preparing || 0, color: "bg-blue-400" },
        { label: "Ready", value: stats.today_total_ready || 0, color: "bg-orange-400" },
        { label: "Delivered", value: stats.today_total_delivered || 0, color: "bg-emerald-400" },
        { label: "Cancel", value: stats.today_total_cancelled || 0, color: "bg-red-400" },
      ].map((item) => (
        <div key={item.label} className="space-y-2">
          <div className={cn("rounded-lg p-3 text-white font-bold text-lg", item.color)}>
            {item.value}
          </div>
          <p className="text-xs text-muted-foreground">{item.label}</p>
        </div>
      ))}
    </div>
  </div>
);

const MonthlyProgress = ({ stats, total }) => (
  <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl p-6 border">
    <div className="flex justify-between items-center mb-4">
      <p className="font-semibold">This Month Performance</p>
      <Badge variant="secondary" className="font-bold">
        ₹{(stats.monthly_revenue || 0).toLocaleString('en-IN')}
      </Badge>
    </div>
    <div className="space-y-3">
      <div className="flex justify-between text-sm">
        <span>Delivered Rate</span>
        <span className="font-medium text-emerald-600">
          {total > 0 ? Math.round((stats.monthly_total_delivered || 0 / total) * 100) : 0}%
        </span>
      </div>
      <div className="w-full bg-muted rounded-full h-3">
        <div
          className="bg-gradient-to-r from-emerald-500 to-teal-500 h-full rounded-full transition-all duration-1000"
          style={{ width: `${total > 0 ? ((stats.monthly_total_delivered || 0) / total) * 100 : 0}%` }}
        />
      </div>
    </div>
  </div>
);

// Modified to use the same stats object as Dashboard
const OrderStats = ({ orderStats, totalOrders }) => {
  return (
    <Card className="border-0 shadow-lg overflow-hidden">
      <CardHeader className="pb-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl font-bold flex items-center gap-2">
              <IndianRupee className="w-6 h-6 text-primary" />
              Order Statistics
            </CardTitle>
          </div>
          <Badge variant="outline" className="text-xs font-medium px-3 py-1">
            Live Data
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="pt-6">
        <Tabs defaultValue="today" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8 h-12 rounded-xl bg-muted/50 p-1">
            <TabsTrigger value="all" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-semibold">
              All Time
            </TabsTrigger>
            <TabsTrigger value="today" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-semibold">
              Today
            </TabsTrigger>
            <TabsTrigger value="monthly" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-semibold">
              This Month
            </TabsTrigger>
          </TabsList>

          {/* ALL TIME */}
          <TabsContent value="all" className="space-y-5 mt-0">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard title="Total Orders" value={totalOrders || 0} icon={ShoppingCart} color="blue" />
              <StatCard title="Delivered" value={orderStats.delivered || 0} icon={Package} color="emerald" />
              <StatCard title="Cancelled" value={orderStats.cancelled || 0} icon={XCircle} color="red" />
              <StatCard
                title="Revenue"
                value={`₹${(orderStats.total_revenue || 0).toLocaleString('en-IN')}`}
                icon={TrendingUp}
                color="green"
                highlight
              />
            </div>
            <StatusProgress stats={orderStats} total={totalOrders || 1} />
          </TabsContent>

          {/* TODAY */}
          <TabsContent value="today" className="space-y-5 mt-0">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard title="Today's Orders" value={orderStats.today_total || 0} icon={Clock} color="purple" />
              <StatCard title="Delivered" value={orderStats.today_total_delivered || 0} icon={CheckCircle2} color="emerald" />
              <StatCard title="Ready" value={orderStats.today_total_ready || 0} icon={Coffee} color="orange" />
              <StatCard
                title="Today's Revenue"
                value={`₹${(orderStats.today_revenue || 0).toLocaleString('en-IN')}`}
                icon={IndianRupee}
                color="green"
                highlight
              />
            </div>
            <TodayStatusBars stats={orderStats} />
          </TabsContent>

          {/* MONTHLY */}
          <TabsContent value="monthly" className="space-y-5 mt-0">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard title="Monthly Orders" value={orderStats.monthly_total || 0} icon={Calendar} color="indigo" />
              <StatCard title="Delivered" value={orderStats.monthly_total_delivered || 0} icon={Truck} color="emerald" />
              <StatCard title="Cancelled" value={orderStats.monthly_total_cancelled || 0} icon={Ban} color="red" />
              <StatCard
                title="Monthly Revenue"
                value={`₹${(orderStats.monthly_revenue || 0).toLocaleString('en-IN')}`}
                icon={ArrowUpRight}
                color="green"
                highlight
              />
            </div>
            <MonthlyProgress stats={orderStats} total={orderStats.monthly_total || 1} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default OrderStats;